import { Container, Graphics } from 'pixi.js';
import { GameState } from './game-state.js';
import { PlayerState } from '@push.io/shared';
import {
  calculateSpeedRatio,
  type WorldVisualQuality,
} from './movement-visuals.js';
import { PlayerView } from './cosmetics/PlayerView.js';
import { GraphicsLevel, GraphicsProfile, selectProfile, autoDetectLevel } from './cosmetics/graphics-quality.js';
import { AssetManager } from './cosmetics/AssetManager.js';

const ARENA_FILL = 0x1a1a4e;
const ORB_COLOR = 0x4dc9f6;

interface InterpState {
  fromX: number; fromY: number;
  toX: number; toY: number;
  t: number;
}

interface OrbBurst {
  x: number;
  y: number;
  elapsed: number;
}

const DEFAULT_APPEARANCE = {
  baseColorId: 'base_color_blue', skinId: null, patternId: null, flagId: null, letterId: null,
  accessoryId: null, trailId: null, nameplateId: null, badgeId: null, victoryEffectId: null,
};

export class GameRenderer {
  private state: GameState;
  private container: Container | null = null;
  private arenaLayer: Graphics = new Graphics();
  private worldTextureLayer: Graphics = new Graphics();
  private worldGridLayer: Graphics = new Graphics();
  private decorativeMarksLayer: Graphics = new Graphics();
  private worldEffectsLayer: Graphics = new Graphics();
  private orbLayer: Graphics = new Graphics();
  private playerViewRoot: Container = new Container();
  private uiLayer: Graphics = new Graphics();
  private cameraX: number = 0;
  private cameraY: number = 0;
  private scale: number = 1;
  private interpTimer: number = 0;
  private interpDuration: number = 50;
  private playerInterp: Map<string, InterpState> = new Map();
  private cameraReady: boolean = false;
  private flameCount: number = 16;
  private orbGlow: boolean = true;
  private worldQuality: WorldVisualQuality = 'high';
  private orbBursts: OrbBurst[] = [];
  private playerViews: Map<string, PlayerView> = new Map();
  private graphicsProfile: GraphicsProfile = selectProfile(GraphicsLevel.MEDIUM);
  private assetManagerInitialized = false;

  constructor(state: GameState) {
    this.state = state;
  }

  async init(container: Container): Promise<void> {
    this.container = container;
    container.addChild(this.arenaLayer);
    container.addChild(this.worldTextureLayer);
    container.addChild(this.worldGridLayer);
    container.addChild(this.decorativeMarksLayer);
    container.addChild(this.worldEffectsLayer);
    container.addChild(this.orbLayer);
    container.addChild(this.playerViewRoot);
    container.addChild(this.uiLayer);

    this.graphicsProfile = selectProfile(autoDetectLevel());

    if (!this.assetManagerInitialized) {
      this.assetManagerInitialized = true;
      await AssetManager.initialize().catch((err) => {
        console.warn('[Renderer] Asset init failed; using base color fallbacks', err);
      });
    }
  }

  resetCamera(): void {
    this.cameraReady = false;
  }

  setQuality(
    flameCount: number,
    orbGlow: boolean,
    worldQuality: WorldVisualQuality,
  ): void {
    this.flameCount = flameCount;
    this.orbGlow = orbGlow;
    this.worldQuality = worldQuality;
    const level = worldQuality === 'low'
      ? GraphicsLevel.LOW
      : worldQuality === 'medium'
        ? GraphicsLevel.MEDIUM
        : GraphicsLevel.HIGH;
    this.setGraphicsLevel(level);
  }

  setGraphicsLevel(level: GraphicsLevel): void {
    this.graphicsProfile = selectProfile(level);
    for (const view of this.playerViews.values()) {
      view.setQuality(this.graphicsProfile);
    }
  }

  emitOrbCollect(x: number, y: number): void {
    this.orbBursts.push({ x, y, elapsed: 0 });
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return { x: this.toScreenX(x), y: this.toScreenY(y) };
  }

  onSnapshotApplied(): void {
    const localPlayer = this.state.getLocalPlayer();

    if (localPlayer && !this.cameraReady) {
      this.cameraX = localPlayer.x;
      this.cameraY = localPlayer.y;
      this.cameraReady = true;
    }

    for (const player of this.state.players.values()) {
      const existing = this.playerInterp.get(player.id);
      if (existing) {
        existing.fromX = existing.fromX + (existing.toX - existing.fromX) * existing.t;
        existing.fromY = existing.fromY + (existing.toY - existing.fromY) * existing.t;
        existing.toX = player.x;
        existing.toY = player.y;
        existing.t = 0;
      } else {
        this.playerInterp.set(player.id, {
          fromX: player.x, fromY: player.y,
          toX: player.x, toY: player.y,
          t: 1,
        });
      }
    }

    for (const id of this.playerInterp.keys()) {
      if (!this.state.players.has(id)) {
        this.playerInterp.delete(id);
      }
    }

    this.syncPlayerViews();

    this.interpTimer = 0;
  }

  private syncPlayerViews(): void {
    const currentIds = new Set(this.state.players.keys());

    // Remove views for players no longer present
    for (const [id, view] of this.playerViews) {
      if (!currentIds.has(id)) {
        this.recycleView(view);
        this.playerViews.delete(id);
      }
    }

    // Create or ensure views for current players
    for (const player of this.state.players.values()) {
      let view = this.playerViews.get(player.id);
      if (!view) {
        view = this.acquireView(player.id);
        this.playerViews.set(player.id, view);
        this.playerViewRoot.addChild(view.root);
        view.setQuality(this.graphicsProfile);
      }
      view.root.visible = player.alive;

      const isLocal = player.id === this.state.playerId;
      const appearance = this.state.playerAppearances.get(player.id) ?? DEFAULT_APPEARANCE;
      view.updateLayers(appearance, Math.max(4, player.radius * this.scale), isLocal, player.name);
    }
  }

  private acquireView(playerId: string): PlayerView {
    return new PlayerView(playerId);
  }

  private recycleView(view: PlayerView): void {
    this.playerViewRoot.removeChild(view.root);
    view.destroy();
  }

  private clearAllViews(): void {
    for (const view of this.playerViews.values()) {
      this.playerViewRoot.removeChild(view.root);
      view.destroy();
    }
    this.playerViews.clear();
  }

  render(deltaMs: number): void {
    if (!this.container) return;
    this.syncPlayerViews();
    this.updateInterpolation(Math.min(deltaMs, 100));
    this.updateCamera();
    this.renderArena();
    this.renderWorldTexture();
    this.renderWorldGrid();
    this.renderDecorativeMarks();
    this.renderWorldEffects(deltaMs);
    this.renderOrbs();
    this.renderPlayers(deltaMs);
    this.renderUI();
  }

  private updateInterpolation(deltaMs: number): void {
    this.interpTimer += deltaMs;
    const t = Math.min(1, this.interpTimer / this.interpDuration);

    for (const interp of this.playerInterp.values()) {
      interp.t = t;
    }
  }

  private updateCamera(): void {
    const localPlayer = this.state.getLocalPlayer();
    if (localPlayer && this.cameraReady) {
      this.cameraX = this.getSmoothX(localPlayer);
      this.cameraY = this.getSmoothY(localPlayer);
    }

    this.scale = Math.max(window.innerWidth, window.innerHeight) / 28;
  }

  private getSmoothX(player: PlayerState): number {
    const interp = this.playerInterp.get(player.id);
    if (!interp || interp.t >= 1) return player.x;
    return interp.fromX + (interp.toX - interp.fromX) * interp.t;
  }

  private getSmoothY(player: PlayerState): number {
    const interp = this.playerInterp.get(player.id);
    if (!interp || interp.t >= 1) return player.y;
    return interp.fromY + (interp.toY - interp.fromY) * interp.t;
  }

  private toScreenX(gameX: number): number {
    return (gameX - this.cameraX) * this.scale + (window.innerWidth / 2);
  }

  private toScreenY(gameY: number): number {
    return (gameY - this.cameraY) * this.scale + (window.innerHeight / 2);
  }

  private renderArena(): void {
    const g = this.arenaLayer;
    g.clear();

    const arena = this.state.arena;
    const cx = this.toScreenX(arena.centerX);
    const cy = this.toScreenY(arena.centerY);
    const radius = arena.radius * this.scale;
    const now = Date.now();

    g.circle(cx, cy, radius);
    g.fill({ color: ARENA_FILL, alpha: 0.6 });

    g.circle(cx, cy, radius);
    g.stroke({ width: 3, color: 0xff0000, alpha: 0.9 });

    g.circle(cx, cy, radius + 4);
    g.stroke({ width: 6, color: 0xff4400, alpha: 0.2 });

    g.circle(cx, cy, radius + 8);
    g.stroke({ width: 8, color: 0xff8800, alpha: 0.1 });

    g.circle(cx, cy, radius + 12);
    g.stroke({ width: 10, color: 0xffcc00, alpha: 0.05 });

    for (const inset of [1.25, 2.6]) {
      const innerRadius = Math.max(0, (arena.radius - inset) * this.scale);
      g.circle(cx, cy, innerRadius);
      g.stroke({ width: 1, color: 0xff6677, alpha: inset < 2 ? 0.11 : 0.055 });
    }

    const markerCount = this.worldQuality === 'high' ? 48 : this.worldQuality === 'medium' ? 32 : 20;
    for (let index = 0; index < markerCount; index++) {
      const angle = index / markerCount * Math.PI * 2;
      const outer = radius - 5;
      const inner = outer - (index % 4 === 0 ? 8 : 4);
      g.moveTo(cx + Math.cos(angle) * inner, cy + Math.sin(angle) * inner);
      g.lineTo(cx + Math.cos(angle) * outer, cy + Math.sin(angle) * outer);
      g.stroke({ width: 1, color: 0xff6677, alpha: index % 4 === 0 ? 0.18 : 0.08 });
    }

    const flameCount = this.flameCount;
    for (let i = 0; i < flameCount; i++) {
      const angle = (i / flameCount) * Math.PI * 2 + Math.sin(now * 0.003 + i) * 0.3;
      const flicker = 0.7 + Math.sin(now * 0.005 + i * 2.3) * 0.3;
      const fx = cx + Math.cos(angle) * (radius + 6 + flicker * 8);
      const fy = cy + Math.sin(angle) * (radius + 6 + flicker * 8);
      const fr = 3 + flicker * 4;

      g.circle(fx, fy, fr * 1.5);
      g.fill({ color: 0xff8800, alpha: 0.15 * flicker });
      g.circle(fx, fy, fr);
      g.fill({ color: 0xff4400, alpha: 0.3 * flicker });
      g.circle(fx, fy, fr * 0.5);
      g.fill({ color: 0xffcc00, alpha: 0.5 * flicker });
    }
  }

  private renderWorldTexture(): void {
    const g = this.worldTextureLayer;
    g.clear();
    const arena = this.state.arena;
    const count = this.worldQuality === 'high' ? 22 : this.worldQuality === 'medium' ? 14 : 7;

    for (let index = 0; index < count; index++) {
      const angle = this.hash(index * 3 + 1) * Math.PI * 2;
      const spotRadiusWorld = 1.8 + this.hash(index * 3 + 2) * 3.6;
      const distance = Math.sqrt(this.hash(index * 3 + 3))
        * Math.max(0, arena.radius - spotRadiusWorld);
      const x = this.toScreenX(arena.centerX + Math.cos(angle) * distance);
      const y = this.toScreenY(arena.centerY + Math.sin(angle) * distance);
      const radius = spotRadiusWorld * this.scale;
      g.circle(x, y, radius);
      g.fill({
        color: index % 2 === 0 ? 0x35357c : 0x11113c,
        alpha: this.worldQuality === 'low' ? 0.025 : 0.038,
      });
    }
  }

  private renderWorldGrid(): void {
    const g = this.worldGridLayer;
    g.clear();
    const arena = this.state.arena;
    const cx = this.toScreenX(arena.centerX);
    const cy = this.toScreenY(arena.centerY);
    const speedRatio = this.getSpeedRatio(this.state.getLocalPlayer());
    const desktopGridBoost = window.innerWidth >= 769 ? 1.55 : 1;
    const alpha = Math.min(0.24, (0.025 + speedRatio * 0.12) * desktopGridBoost);
    const gridColor = speedRatio > 0.08 ? 0x9292e8 : 0x7777bb;
    const gridWidth = window.innerWidth >= 769 ? 1.25 : 1;
    const ringStep = this.worldQuality === 'low' ? 6 : this.worldQuality === 'medium' ? 4 : 3;

    for (let radius = ringStep; radius < arena.radius - 1; radius += ringStep) {
      g.circle(cx, cy, radius * this.scale);
      g.stroke({ width: gridWidth, color: gridColor, alpha });
    }

    const radialStep = this.worldQuality === 'high' ? 2 : this.worldQuality === 'medium' ? 3 : 5;
    const angleCount = this.worldQuality === 'high' ? 20 : this.worldQuality === 'medium' ? 14 : 10;
    for (let radius = radialStep; radius < arena.radius - 1; radius += radialStep) {
      for (let index = 0; index < angleCount; index++) {
        if ((radius + index) % 3 === 0) continue;
        const angle = index / angleCount * Math.PI * 2 + radius * 0.17;
        const x = this.toScreenX(arena.centerX + Math.cos(angle) * radius);
        const y = this.toScreenY(arena.centerY + Math.sin(angle) * radius);
        if (!this.isOnScreen(x, y, 8)) continue;
        g.circle(x, y, this.worldQuality === 'high' ? 1.35 : 1);
        g.fill({ color: 0xb0b0f0, alpha: Math.min(0.24, alpha * 1.35) });
      }
    }
  }

  private renderDecorativeMarks(): void {
    const g = this.decorativeMarksLayer;
    g.clear();
    const arena = this.state.arena;
    const count = this.worldQuality === 'high' ? 38 : this.worldQuality === 'medium' ? 22 : 10;

    for (let index = 0; index < count; index++) {
      const angle = this.hash(index * 4 + 91) * Math.PI * 2;
      const distance = Math.sqrt(this.hash(index * 4 + 92)) * Math.max(0, arena.radius - 2);
      const worldX = arena.centerX + Math.cos(angle) * distance;
      const worldY = arena.centerY + Math.sin(angle) * distance;
      const x = this.toScreenX(worldX);
      const y = this.toScreenY(worldY);
      if (!this.isOnScreen(x, y, 18)) continue;
      const rotation = this.hash(index * 4 + 93) * Math.PI * 2;
      const length = (0.16 + this.hash(index * 4 + 94) * 0.34) * this.scale;

      const radius = Math.max(2, length * (0.35 + this.hash(index + 701) * 0.35));
      g.arc(x, y, radius, rotation, rotation + Math.PI * (0.55 + this.hash(index + 702) * 0.75));
      g.stroke({ width: 1, color: 0x6868a8, alpha: 0.09 });
    }
  }

  private renderWorldEffects(deltaMs: number): void {
    const g = this.worldEffectsLayer;
    g.clear();

    for (let index = this.orbBursts.length - 1; index >= 0; index--) {
      const burst = this.orbBursts[index];
      burst.elapsed += deltaMs;
      if (burst.elapsed >= 520) {
        this.orbBursts.splice(index, 1);
        continue;
      }
      const progress = burst.elapsed / 520;
      const x = this.toScreenX(burst.x);
      const y = this.toScreenY(burst.y);
      g.circle(x, y, (0.35 + progress * 1.15) * this.scale);
      g.stroke({ width: 2, color: ORB_COLOR, alpha: (1 - progress) * 0.35 });
      g.circle(x, y, Math.max(2, (1 - progress) * 0.28 * this.scale));
      g.fill({ color: ORB_COLOR, alpha: (1 - progress) * 0.22 });
    }
  }

  private renderOrbs(): void {
    const g = this.orbLayer;
    g.clear();

    for (const orb of this.state.orbs) {
      const x = this.toScreenX(orb.x);
      const y = this.toScreenY(orb.y);
      const r = Math.max(2, orb.radius * this.scale);

      g.circle(x, y, r);
      g.fill({ color: ORB_COLOR, alpha: 0.9 });

      if (this.orbGlow) {
        g.circle(x, y, r * 2);
        g.fill({ color: ORB_COLOR, alpha: 0.15 });
      }
    }
  }

  private renderPlayers(deltaMs: number): void {
    for (const player of this.state.players.values()) {
      if (!player.alive) continue;
      const view = this.playerViews.get(player.id);
      if (!view) continue;

      const screenX = this.toScreenX(this.getSmoothX(player));
      const screenY = this.toScreenY(this.getSmoothY(player));
      view.root.position.set(screenX, screenY);

      const appearance = this.state.playerAppearances.get(player.id) ?? DEFAULT_APPEARANCE;
      view.updateTrail(deltaMs, screenX, screenY, player.vx ?? 0, player.vy ?? 0, appearance, this.graphicsProfile);
    }
  }

  private renderUI(): void {
    const g = this.uiLayer;
    g.clear();
  }

  private getSpeedRatio(player: PlayerState | null): number {
    if (!player) return 0;
    return calculateSpeedRatio(player.vx, player.vy, player.upgrades.speed);
  }

  private isOnScreen(x: number, y: number, margin: number): boolean {
    return x >= -margin && x <= window.innerWidth + margin
      && y >= -margin && y <= window.innerHeight + margin;
  }

  private hash(seed: number): number {
    const value = Math.sin(seed * 91.3458 + 12.345) * 43758.5453;
    return value - Math.floor(value);
  }
}
